import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Search, ArrowRight, FileText, Download, X, Lock, Loader2, Link2, QrCode } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

const DATE_LOCALES = { fr: "fr-FR", en: "en-US", es: "es-ES", it: "it-IT", pt: "pt-PT" };

export default function NewsList() {
  const { lang, t } = useLang();
  const [news, setNews] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("published_at-desc");
  // Publications RGPD form modal state
  const [modalPub, setModalPub] = useState(null);
  // Detail modal (description) — opened when user clicks on the card body
  const [detailPub, setDetailPub] = useState(null);
  // QR Code modal — opened from the immersive reader
  const [qrPub, setQrPub] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", phone: "", job_title: "", consent_rgpd: false });
  const [submitting, setSubmitting] = useState(false);

  const CATEGORIES_OPTS = [
    { val: "all", label: t("filter_all") },
    { val: "Innovation", label: t("cat_innovation") },
    { val: "Technologie", label: t("cat_technology") },
    { val: "Startup", label: t("cat_startup") },
    { val: "Marché", label: t("cat_market") },
    { val: "Digital", label: t("cat_digital") },
    { val: "Communiqué", label: t("cat_announcement") },
  ];

  useEffect(() => {
    const [s, o] = sort.split("-");
    const params = { lang, sort: s, order: o };
    if (cat !== "all") params.category = cat;
    if (q) params.q = q;
    api.get("/news", { params }).then((r) => setNews(r.data)).catch(() => {});
  }, [cat, q, lang, sort]);

  // Load publications (news with a pdf_url)
  useEffect(() => {
    api.get("/publications", { params: { limit: 20 } }).then((r) => setPubs(r.data || [])).catch(() => {});
  }, []);

  // Filter publications out of the news grid — they have their own section below
  const pubSlugs = new Set(pubs.map((p) => p.slug));
  const newsOnly = news.filter((n) => !pubSlugs.has(n.slug));

  // Scroll to anchored publication on load (e.g. /news#publication-livre-blanc-…)
  useEffect(() => {
    if (pubs.length === 0) return;
    const hash = window.location.hash || "";
    if (!hash.startsWith("#publication-")) return;
    const slug = hash.replace("#publication-", "");
    const target = pubs.find((p) => p.slug === slug);
    if (!target) return;
    // Wait a tick to ensure the DOM has rendered the section
    setTimeout(() => {
      const el = document.getElementById(`publication-${slug}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setDetailPub(target);
      }
    }, 250);
  }, [pubs]);

  const copyPubLink = (pub) => {
    // Public landing page (HTML) — branded presentation with hero, description, cover, CTA download.
    const url = `${window.location.origin}/news/${pub.slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Lien direct vers la page du livre blanc copié"),
      () => toast.error("Impossible de copier le lien"),
    );
  };

  const downloadQR = async (pub) => {
    try {
      const res = await fetch(`${BACKEND}/api/news/${pub.slug}/qr?size=10`);
      if (!res.ok) throw new Error("QR fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pub.slug}-qr.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("QR Code téléchargé");
    } catch {
      toast.error("Erreur de téléchargement du QR");
    }
  };

  // Immersive reader: Esc to close + lock background scroll
  useEffect(() => {
    if (!detailPub) return;
    const onKey = (e) => { if (e.key === "Escape") setDetailPub(null); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [detailPub]);

  // Lightweight Markdown → HTML renderer (titles, bold, italic, links, paragraphs)
  const renderMarkdown = (md) => {
    if (!md) return "";
    let html = md
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/^### (.*)$/gm, '<h3 class="mt-6 text-xl font-bold text-white">$1</h3>')
      .replace(/^## (.*)$/gm, '<h2 class="mt-7 text-2xl font-bold text-white">$1</h2>')
      .replace(/^# (.*)$/gm, '<h1 class="mt-8 text-3xl font-bold text-white">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-wine-300 underline hover:text-wine-200">$1</a>')
      .replace(/\n\n/g, '</p><p class="mt-4">')
      .replace(/\n/g, "<br/>");
    return `<p>${html}</p>`;
  };

  const openPubModal = (pub) => {
    if (!pub.lead_capture_enabled) {
      // Direct download — uses stable canonical URL which redirects to the real PDF
      window.open(`${BACKEND}/api/news/${pub.slug}/pdf`, "_blank");
      return;
    }
    setModalPub(pub);
    setForm({ first_name: "", last_name: "", email: "", company: "", phone: "", job_title: "", consent_rgpd: false });
  };

  const submitDownload = async (e) => {
    e.preventDefault();
    if (!form.consent_rgpd) { toast.error("Le consentement RGPD est requis"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/news/${modalPub.slug}/request-download`, form);
      toast.success("📧 Lien envoyé par email + téléchargement immédiat");
      window.open(data.pdf_url, "_blank");
      setModalPub(null);
      // Refresh download count
      api.get("/publications").then((r) => setPubs(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
    setSubmitting(false);
  };

  return (
    <PublicLayout>
      <Seo title={t("news_seo_title")} description={t("news_seo_desc")} path="/news" />
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-8">
          <div className="h-1 w-12 rounded-full bg-wine-500" />
          <h1 className="mt-3 text-4xl font-bold text-navy md:text-5xl">{t("news_title")}</h1>
          <p className="mt-3 max-w-2xl text-neutral-600">{t("news_subtitle")}</p>
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("news_search_placeholder")} className="w-full rounded-full border border-neutral-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-wine-300" data-testid="news-search-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES_OPTS.map((c) => (
              <button key={c.val} onClick={() => setCat(c.val)} className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${cat === c.val ? "bg-wine-500 text-white" : "bg-white text-neutral-600 border border-neutral-200 hover:bg-wine-50"}`} data-testid={`news-category-${c.val.toLowerCase()}`}>
                {c.label}
              </button>
            ))}
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="news-sort">
            <option value="published_at-desc">{t("comp_newslist_90")}</option>
            <option value="published_at-asc">Plus anciennes d&apos;abord</option>
            <option value="title-asc">Titre A → Z</option>
            <option value="title-desc">Titre Z → A</option>
          </select>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {newsOnly.map((n, i) => (
            <Link key={n.id} to={`/news/${n.slug}`} className="group overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`news-card-${i}`}>
              <div className="aspect-[16/10] overflow-hidden bg-wine-50">
                <img src={resolveUrl(n.image)} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between text-xs">
                  <span className="rounded-full bg-wine-50 px-2.5 py-1 font-semibold uppercase tracking-wider text-wine-700">{n.category}</span>
                  <span className="text-neutral-500">{n.published_at && new Date(n.published_at).toLocaleDateString(DATE_LOCALES[lang] || "fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span>
                </div>
                <h3 className="mt-3 font-serif text-xl leading-snug text-neutral-900 group-hover:text-wine-700">{n.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-neutral-600">{n.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-wine-600 group-hover:gap-2 transition-all">
                  {t("read_more")} <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Sprint 70 — Publications / Livres blancs (section under News) */}
        {pubs.length > 0 && (
          <section className="mt-16" data-testid="publications-section">
            <div className="border-t border-wine-100 pt-12">
              <div className="h-1 w-12 rounded-full bg-wine-500" />
              <div className="mt-3 flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-3xl font-bold text-navy md:text-4xl inline-flex items-center gap-2">
                    <FileText className="h-7 w-7 text-wine-600" /> Publications
                  </h2>
                  <p className="mt-2 max-w-2xl text-neutral-600">Téléchargez nos livres blancs et études sectorielles — analyses approfondies pour décider, anticiper, innover.</p>
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-wine-600">{pubs.length} publication{pubs.length > 1 ? "s" : ""}</div>
              </div>

              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="publications-grid">
                {pubs.map((p) => {
                  const coverSrc = p.pdf_thumbnail ? ((p.pdf_thumbnail.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL : "") + p.pdf_thumbnail) : (p.image ? resolveUrl(p.image) : null);
                  return (
                    <article id={`publication-${p.slug}`} key={p.id} className="group rounded-2xl border border-wine-100 bg-white overflow-hidden hover:shadow-lg transition-all scroll-mt-24" data-testid={`publication-${p.slug}`}>
                      <button type="button" onClick={() => setDetailPub(p)} className="w-full text-left" data-testid={`publication-open-${p.slug}`}>
                        {coverSrc && (
                          <div className="relative aspect-[3/4] sm:aspect-[16/10] overflow-hidden bg-wine-50">
                            <img src={coverSrc} alt={p.title} className={`h-full w-full ${p.pdf_thumbnail ? "object-contain p-2 sm:p-3" : "object-cover"} group-hover:scale-105 transition-transform duration-500`} />
                            {p.lead_capture_enabled && (
                              <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white shadow"><Lock className="h-2.5 w-2.5" />RGPD</span>
                            )}
                          </div>
                        )}
                        <div className="p-5">
                          <div className="flex items-center justify-between text-[11px] text-neutral-500">
                            <span className="rounded-full bg-wine-50 px-2.5 py-1 font-semibold uppercase tracking-wider text-wine-700">{p.category || "Livre blanc"}</span>
                            <span>📥 {p.download_count || 0} téléchargement{(p.download_count || 0) > 1 ? "s" : ""}</span>
                          </div>
                          <h3 className="mt-3 font-serif text-xl leading-snug text-neutral-900 group-hover:text-wine-700">{p.title}</h3>
                          <p className="mt-2 line-clamp-3 text-sm text-neutral-600">{p.excerpt}</p>
                        </div>
                      </button>
                      <div className="flex items-center gap-2 px-5 pb-5">
                        <button onClick={() => openPubModal(p)}
                                className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600"
                                data-testid={`download-publication-${p.slug}`}>
                          <Download className="h-3.5 w-3.5" /> Télécharger le PDF
                        </button>
                        <button onClick={() => copyPubLink(p)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 bg-white px-3 py-2 text-xs font-semibold text-wine-700 hover:bg-wine-50"
                                title="Copier le lien direct vers cette publication"
                                data-testid={`share-publication-${p.slug}`}>
                          <Link2 className="h-3.5 w-3.5" /> Partager
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </section>

      {/* Immersive detail modal — full-screen reader opened when a publication card is clicked */}
      {detailPub && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-br from-navy via-[#1a1f3a] to-[#0e1228] animate-in fade-in duration-300"
          data-testid="publication-detail-modal"
          role="dialog"
          aria-modal="true"
        >
          {/* Decorative wine glow */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-wine-500/20 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-wine-700/30 blur-3xl" />

          {/* Close (sticky top-right) */}
          <button
            onClick={() => setDetailPub(null)}
            className="fixed top-5 right-5 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md hover:bg-white/20"
            data-testid="close-detail-modal"
          >
            <X className="h-4 w-4" /> Fermer <span className="ml-1 hidden text-[10px] opacity-70 sm:inline">Échap</span>
          </button>

          <div className="relative mx-auto max-w-5xl px-6 py-16 animate-in zoom-in-95 fade-in duration-500">
            {/* Breadcrumb */}
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-wine-300">Publication / {detailPub.category || "Livre blanc"}</div>

            <div className="mt-4 grid gap-10 lg:grid-cols-[280px_1fr] lg:gap-14">
              {/* Cover */}
              <div className="lg:sticky lg:top-10 lg:self-start">
                {detailPub.pdf_thumbnail ? (
                  <img
                    src={(detailPub.pdf_thumbnail.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL : "") + detailPub.pdf_thumbnail}
                    alt={`Couverture ${detailPub.title}`}
                    className="w-full max-w-[280px] rounded-xl border border-white/10 shadow-2xl shadow-black/50 ring-1 ring-white/10"
                    data-testid="detail-pdf-thumb"
                  />
                ) : (
                  <div className="aspect-[3/4] w-full max-w-[280px] grid place-items-center rounded-xl border border-white/10 bg-white/5 text-white/40">
                    <FileText className="h-16 w-16" />
                  </div>
                )}
                <div className="mt-4 flex items-center gap-2 text-xs text-white/60">
                  <Download className="h-3.5 w-3.5 text-wine-300" />
                  <span>{detailPub.download_count || 0} téléchargement{(detailPub.download_count || 0) > 1 ? "s" : ""}</span>
                  {detailPub.lead_capture_enabled && (
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-200"><Lock className="h-2.5 w-2.5" />RGPD</span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="text-white">
                <h1 className="font-serif text-4xl leading-tight md:text-5xl" data-testid="detail-title">{detailPub.title}</h1>

                <div className="mt-8">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-wine-300">Présentation</div>
                  <p className="mt-3 whitespace-pre-line text-lg leading-relaxed text-white/90" data-testid="detail-excerpt">{detailPub.excerpt}</p>
                </div>

                {detailPub.body && (
                  <div className="mt-10">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-wine-300">Description détaillée</div>
                    <div
                      className="prose prose-invert mt-3 max-w-none text-base leading-relaxed text-white/80 [&_p]:my-3"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(detailPub.body) }}
                      data-testid="detail-body"
                    />
                  </div>
                )}

                {detailPub.tags?.length > 0 && (
                  <div className="mt-10 flex flex-wrap gap-2">
                    {detailPub.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-wine-200">#{t}</span>
                    ))}
                  </div>
                )}

                {/* Bottom CTA (inline, generous spacing) */}
                <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-white/10 pt-8">
                  <button
                    onClick={() => { const p = detailPub; setDetailPub(null); openPubModal(p); }}
                    className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-wine-500/30 transition-all hover:bg-wine-600 hover:shadow-wine-500/50"
                    data-testid="detail-download-btn"
                  >
                    <Download className="h-4 w-4" /> Télécharger le PDF
                  </button>
                  <button
                    onClick={() => copyPubLink(detailPub)}
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md hover:bg-white/10"
                    data-testid="detail-share-btn"
                  >
                    <Link2 className="h-4 w-4" /> Copier le lien direct
                  </button>
                  <button
                    onClick={() => setQrPub(detailPub)}
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md hover:bg-white/10"
                    data-testid="detail-qr-btn"
                    title="Afficher le QR Code"
                  >
                    <QrCode className="h-4 w-4" /> QR Code
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code modal — pops over the immersive reader */}
      {qrPub && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/80 p-4 animate-in fade-in duration-200" onClick={() => setQrPub(null)} data-testid="publication-qr-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">QR Code de partage</div>
                <h3 className="mt-1 text-lg font-bold text-navy">{qrPub.title}</h3>
              </div>
              <button onClick={() => setQrPub(null)} className="rounded-full p-1 hover:bg-neutral-100" data-testid="close-qr-modal"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid place-items-center rounded-2xl border-2 border-wine-100 bg-wine-50/40 p-6">
              <img
                src={`${BACKEND}/api/news/${qrPub.slug}/qr?size=10`}
                alt={`QR Code ${qrPub.title}`}
                className="h-64 w-64"
                data-testid="qr-image"
              />
            </div>
            <p className="mt-3 text-xs text-neutral-600 text-center">
              Scanne ce QR pour télécharger directement le livre blanc. Idéal pour les salons, kakémonos, flyers, badges.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <button onClick={() => downloadQR(qrPub)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="qr-download-btn">
                <Download className="h-4 w-4" /> Télécharger le PNG (HD)
              </button>
              <button onClick={() => copyPubLink(qrPub)} className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 bg-white px-4 py-2 text-sm font-semibold text-wine-700 hover:bg-wine-50" data-testid="qr-copy-link-btn">
                <Link2 className="h-4 w-4" /> Copier le lien
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RGPD form modal */}
      {modalPub && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="publication-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {modalPub.pdf_thumbnail && (
                  <img
                    src={(modalPub.pdf_thumbnail.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL : "") + modalPub.pdf_thumbnail}
                    alt="Aperçu du PDF"
                    className="h-20 w-auto shrink-0 rounded-lg border border-neutral-200 shadow-sm"
                    data-testid="modal-pdf-thumb"
                  />
                )}
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">Téléchargement sécurisé</div>
                  <h3 className="mt-1 text-xl font-bold text-navy">{modalPub.title}</h3>
                </div>
              </div>
              <button onClick={() => setModalPub(null)} className="rounded-full p-1 hover:bg-neutral-100" data-testid="close-publication-modal"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-2 text-xs text-neutral-600">Renseignez vos coordonnées pour recevoir le lien de téléchargement par email + accès immédiat au PDF.</p>

            <form onSubmit={submitDownload} className="mt-5 grid gap-3 grid-cols-2">
              <Field label="Prénom *" v={form.first_name} on={(v) => setForm({ ...form, first_name: v })} req test="pub-firstname" />
              <Field label="Nom *" v={form.last_name} on={(v) => setForm({ ...form, last_name: v })} req test="pub-lastname" />
              <div className="col-span-2"><Field label="Email professionnel *" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} req test="pub-email" /></div>
              <div className="col-span-2"><Field label="Entreprise *" v={form.company} on={(v) => setForm({ ...form, company: v })} req test="pub-company" /></div>
              <Field label="Fonction" v={form.job_title} on={(v) => setForm({ ...form, job_title: v })} test="pub-jobtitle" />
              <Field label="Téléphone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} test="pub-phone" />

              <label className="col-span-2 flex items-start gap-2 mt-1 cursor-pointer">
                <input type="checkbox" required checked={form.consent_rgpd} onChange={(e) => setForm({ ...form, consent_rgpd: e.target.checked })} className="mt-0.5 h-4 w-4 accent-wine-500" data-testid="pub-consent" />
                <span className="text-[11px] leading-snug text-neutral-700">
                  J&apos;accepte que mes données soient utilisées par <strong>La WineTech</strong> pour me contacter au sujet de cette publication et des actualités sectorielles. Vous pouvez vous désinscrire à tout moment. *
                </span>
              </label>

              <button type="submit" disabled={submitting} className="col-span-2 mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-wine-500 px-5 py-3 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="pub-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {submitting ? "Envoi…" : "Recevoir le PDF + télécharger"}
              </button>
            </form>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function Field({ label, v, on, type = "text", req, test }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
      <input
        type={type} required={req} value={v}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-wine-300"
        data-testid={test}
      />
    </div>
  );
}
